import {useDrag} from "react-dnd";
import React, {useEffect, useRef} from "react";
import {getEmptyImage} from "react-dnd-html5-backend";
import {Flex} from "components/common_style/flex";
import FreeTransform from 'react-free-transform'
import 'components/placed_item/free_transform_styles.css'
import 'components/placed_item/free_move_styles.css'
import {TopButton} from "pages/story_editor/story_editor_styles";
import {ReactComponent as Delete} from "assets/icons/delete.svg";
import {ReactComponent as FlipBack} from "assets/icons/flip_back.svg";
import {ReactComponent as FlipFront} from "assets/icons/flip_front.svg";
import styled from "styled-components";
import Types from "components/editor_canvas/editor_canvas_element_types";
import EditorCanvas from "components/editor_canvas";
import {fullTextBoxes, MAsset} from "components/tools_pane/tools_items";
import {mdiFlipHorizontal, mdiFlipVertical} from '@mdi/js';
import Icon from '@mdi/react';
import {Localization} from "localizations/languages";

let _freeTransform = require('free-transform');

const iconStyle = {width: '17px', height: '17px', fill: 'white'};

export const placedButtonPosition = {
    left: 'left',
    right: 'right',
};

export const placedButtons = {
    delete: 'delete',
    zindexUp: 'zindex-up',
    zindexDown: 'zindex-down',
    flipW: 'flip-w',
    flipH: 'flip-h',
};

const HMirror = () => <Icon path={mdiFlipHorizontal}
                            title="Flip horizontally"
                            size={'17px'}
                            color="white"/>;

const VMirror = () => <Icon path={mdiFlipVertical}
                            title="Flip vertically"
                            size={'17px'}
                            color="white"/>;

const TEXT_SCALE = 1;

export class PlacedItemUtil {
    static positionToPx(canvasRect, position) {
        let p = {...position};
        let pxPerH = canvasRect.height / 100;
        let pxPerW = canvasRect.width / 100;
        p.width *= pxPerH;
        p.height *= pxPerH;
        p.x *= pxPerW;
        p.y *= pxPerH;
        return p;
    }

    static positionToPercentMut(canvasRect, p) {
        let factor = 100 / canvasRect.height;
        p.y *= factor;
        factor = 100 / canvasRect.width;
        p.x *= factor;
        return p;
    }

    static textToPx(props, tstyles, canvasRect) {
        if (!tstyles) return {};
        let ts = {...tstyles};
        let pxPerH = canvasRect.height / 100;
        pxPerH = (pxPerH * props.scaleY) * (1 / props.scaleX);
        if (ts.fontSize)
            ts.fontSize *= pxPerH * TEXT_SCALE;
        ts.lineHeight = 1;
        return ts;
    }

    static getTextStyles(props, pitem, canvasRect, placeholder) {
        let tstyles;
        if (placeholder) {
            tstyles = {
                fontSize: pitem.uuid === EditorCanvas.STORY_TITLE_ID ? 20 : 20,
                fontWeight: 'bold',
                opacity: 0.6,
            }
        } else {
            tstyles = PlacedItemUtil.textToPx(props, pitem.textOverlay.styles, canvasRect);
        }
        let w = TEXT_SCALE * props.width;
        let h = (TEXT_SCALE * (props.height * props.scaleY) * (1 / props.scaleX));
        return {
            width: w,
            height: h,
            top: 0,
            left: 0,
            ...tstyles,
        }
    }

    static getProps(pitem, canvasRect) {
        let position = PlacedItemUtil.positionToPx(canvasRect, pitem.position);
        return {
            x: position.x,
            y: position.y,
            width: position.width,
            height: position.height,
            scaleX: position.scaleX,
            scaleY: position.scaleY,
            angle: position.rotation,
            disableScale: true,
        };
    }
}

const TBInput = styled.div`
  resize: none;
  background-color: transparent;
  border: none;
  outline: none;
  word-break: break-word;
  text-align: justify;
  overflow: hidden;
  display: inline;
  margin: auto 0 auto 0;
  padding: 0;
  ${(props) => props.metaStyles}
`;

let cachedControls = {};

export const PlacedItemDrag = ({placedItem, onClick}) => {
    const [{isDragging}, drag, preview] = useDrag({
        item: {
            type: placedItem.type,
            placedItem: placedItem
        },
        collect: monitor => ({
            isDragging: !!monitor.isDragging(),
        }),
    });

    useEffect(() => {
        preview(getEmptyImage(), {captureDraggingState: true})
    }, []);

    let rotate = `rotate(${placedItem.position.rotation}rad)`;

    function getStyles() {
        return {
            opacity: isDragging ? 0 : 1,
            position: 'absolute',
            top: placedItem.position.y + '%',
            left: placedItem.position.x + '%',
            width: placedItem.position.width / 1.77 + '%',
            height: placedItem.position.height + '%',
        };
    }

    return <Flex vAlign={'center'} hAlign={'center'} ref={drag} style={getStyles()} onClick={(e) => {
        e.stopPropagation();
        onClick();
    }}>
        <img src={MAsset.get(placedItem).img} style={{
            rotate,
            WebkitTransform: rotate,
            width: '100%',
            height: '100%',
            margin: '0 auto',
            objectFit: 'contain'
        }}/>
    </Flex>;
};

const PlacedItemView = ({
                            placedItem,
                            onClick,
                            onUpdate,
                            onDelete,
                            onMoveUp,
                            onTextChange,
                            onTextSave,
                            onMoveDown,
                            canvasRect,
                            canvasOffsetX,
                            canvasOffsetY,
                            onHMirror,
                            onVMirror,
                            transform = false,
                            onTransformEnd,
                            onTransformStart,
                            textContent,
                            textContentInitial,
                            textFocused,
                            fullTextBox,
                            printable
                        }) => {
    let props = PlacedItemUtil.getProps(placedItem, canvasRect);
    let scale = `scale(${props.scaleX / TEXT_SCALE})`;
    let img_scale = `scale(${!!placedItem.position.h_mirror ? -1 : 1}, ${!!placedItem.position.v_mirror ? -1 : 1})`;

    let showPlaceholder = false;
    if (transform || placedItem.uuid === EditorCanvas.STORY_TITLE_ID) {
        showPlaceholder = textContent.length === 0 && (placedItem.type === Types.PLACED_TEXTBOX || placedItem.type === Types.PLACED_TEXTBOX_FULLSCREEN) && !textFocused;
    }
    let tstyles = PlacedItemUtil.getTextStyles(props, placedItem, canvasRect, showPlaceholder);
    let _styler = _freeTransform.styler(props);
    const ref = useRef();

    let asset_element;
    if (fullTextBox) {
        switch (placedItem.asset) {
            case fullTextBoxes.left:
            case fullTextBoxes.right:
            default:
                asset_element = <div style={{
                    backgroundColor: "white",
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    WebkitTransform: img_scale,
                    img_scale
                }}/>;
                break;

        }
    } else {
        asset_element = placedItem.asset != null ? <img src={MAsset.get(placedItem).img} style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            position: 'absolute',
            WebkitTransform: img_scale,
            img_scale
        }}/> : null;
    }

    return <div key={placedItem.uuid}>
        <div
            style={{
                position: 'absolute',
                zIndex: placedItem.position.zIndex
            }}
            className={'disable-select'}>
            <FreeTransform
                offsetX={canvasOffsetX}
                offsetY={canvasOffsetY}
                aspectRatio={!placedItem.freeTransform && !(placedItem.meta && !!placedItem.meta.freeTransform)}
                onUpdate={!!onUpdate ? (params) => onUpdate(placedItem, params) : () => {
                }}
                onTransformEnd={!!onTransformEnd ? (e) => onTransformEnd(placedItem) : () => {
                }}
                onTransformStart={!!onTransformStart ? (e) => onTransformStart(placedItem) : () => {
                }}
                classPrefix={'fm'}
                {...props}>

                {asset_element}
                <div style={{
                    position: 'absolute',
                    display: 'block',
                    scale,
                    transformOrigin: 'top left',
                    WebkitTransform: scale,
                    overflow: "hidden",
                    ...tstyles
                }}>
                    {!printable && <img src={'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>'} style={{
                        float: 'left',
                        display: 'inline!important',
                        width: '50%',
                        height: '100%',
                        opacity: '0',
                        shapeOutside: `url('${MAsset.get(placedItem).shapeLeft}')`,
                        shapeMargin: '4px',
                    }}/>}
                    {!printable && <img src={'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>'} style={{
                        float: 'right',
                        display: 'inline!important',
                        width: '50%',
                        height: '100%',
                        opacity: '0',
                        shapeOutside: `url('${MAsset.get(placedItem).shapeRight}')`,
                        shapeMargin: '4px',
                    }}/>}
                    <TBInput
                        metaStyles={placedItem.meta && placedItem.meta.styles}
                        style={{
                            maxWidth: tstyles.width,
                            height: tstyles.height,
                            width: tstyles.width,
                            opacity: textFocused ? 0 : 1,
                            maxHeight: tstyles.height,
                        }} {...props}>
                        {showPlaceholder ? placedItem.uuid === EditorCanvas.STORY_TITLE_ID ? "Write a title..." : Localization.current.translation22 : textContent}
                    </TBInput>
                </div>
            </FreeTransform>
        </div>

        {/* transform decorations */}
        {transform ? <div
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onClick(e);
            }}
            style={{
                position: 'absolute',
                zIndex: 999
            }}
            className={'disable-select'}><FreeTransform
            offsetX={canvasOffsetX}
            offsetY={canvasOffsetY}
            aspectRatio={!placedItem.freeTransform && !(placedItem.meta && !!placedItem.meta.freeTransform)}
            onUpdate={(params) => onUpdate(placedItem, params)}
            onTransformEnd={(e) => onTransformEnd(placedItem)}
            onTransformStart={(e) => onTransformStart(placedItem)}
            classPrefix={'tr'}
            {...props}>{getControls(placedItem, onMoveUp, onMoveDown, onHMirror, onVMirror, onDelete)}</FreeTransform>
        </div> : null}

        {/* text input */}
        <div
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                ref.current.focus();
            }}
            style={{
                zIndex: 999,
                display: textFocused ? 'block' : 'none',
                ..._styler.element
            }}
            className={'disable-select'}>
            <div style={{
                position: 'absolute',
                display: 'block',
                scale,
                transformOrigin: 'top left',
                WebkitTransform: scale,
                overflow: "hidden",
                ...tstyles
            }}>
                {!printable && <img src={'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>'} style={{
                    float: 'left',
                    display: 'inline!important',
                    width: '50%',
                    height: '100%',
                    opacity: '0',
                    shapeOutside: `url('${MAsset.get(placedItem).shapeLeft}')`,
                    shapeMargin: '4px',
                }}/>}
                {!printable && <img src={'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>'} style={{
                    float: 'right',
                    display: 'inline!important',
                    width: '50%',
                    height: '100%',
                    opacity: '0',
                    shapeOutside: `url('${MAsset.get(placedItem).shapeRight}')`,
                    shapeMargin: '4px',
                }}/>}
                <TBInput id={placedItem.uuid} placedItem={placedItem} onBlur={(e) => onTextSave(placedItem, e)}
                         onInput={(e) => onTextChange(placedItem, e)}
                         metaStyles={placedItem.meta && placedItem.meta.styles}
                         ref={ref}
                         style={{
                             maxWidth: tstyles.width,
                             width: tstyles.width,
                             height: tstyles.height,
                             maxHeight: tstyles.height,
                             ...(printable ? {
                                 textAlign: 'center',
                                 display: 'table-cell',
                                 verticalAlign: 'middle',
                             } : {})
                         }} {...props} contentEditable="true" suppressContentEditableWarning="true">
                    {textContentInitial}
                </TBInput>
            </div>
        </div>
    </div>;
};

function getControls(placedItem, onMoveUp, onMoveDown, onHMirror, onVMirror, onDelete) {
    if (cachedControls[placedItem.uuid]) {
        return cachedControls[placedItem.uuid];
    }
    let buttons = [];
    let meta = placedItem.meta;
    if (meta) meta = meta.buttons;
    if (!!onMoveUp && (!meta || !!meta[placedButtons.zindexUp])) {
        buttons.push(<TopButton
            title="Move down"
            onClick={(e) => {
                e.stopPropagation();
                onMoveUp(placedItem);
            }}
            style={{
                position: 'absolute',
                top: 'calc(50% + 25px)',
                [!meta ? 'right' : meta[placedButtons.zindexUp]]: -60,
                width: 40,
                height: 40,
            }}>
            <FlipBack style={iconStyle}/>
        </TopButton>)
    }
    if (!!onDelete && (!meta || !!meta[placedButtons.delete])) {
        buttons.push(<TopButton
            title="Delete"
            onClick={(e) => {
                e.stopPropagation();
                onDelete(placedItem);
            }}
            style={{
                position: 'absolute',
                top: 'calc(50% - 20px)',
                [!meta ? 'right' : meta[placedButtons.delete]]: -60,
                width: 40,
                height: 40,
            }}>
            <Delete style={iconStyle}/>
        </TopButton>)
    }
    if (!!onMoveDown && (!meta || !!meta[placedButtons.zindexDown])) {
        buttons.push(<TopButton
            title="Move up"
            onClick={(e) => {
                e.stopPropagation();
                onMoveDown(placedItem);
            }}
            style={{
                position: 'absolute',
                top: 'calc(50% - 65px)',
                [!meta ? 'right' : meta[placedButtons.zindexDown]]: -60,
                width: 40,
                height: 40,
            }}>
            <FlipFront style={iconStyle}/>
        </TopButton>)
    }
    if (!!onHMirror && (!meta || !!meta[placedButtons.flipH])) {
        buttons.push(<TopButton
            onClick={(e) => {
                e.stopPropagation();
                onHMirror(placedItem);
            }}
            style={{
                position: 'absolute',
                top: 'calc(50% - 43px)',
                [!meta ? 'left' : meta[placedButtons.flipH]]: -60,
                width: 40,
                height: 40,
            }}>
            <HMirror/>
        </TopButton>)
    }
    if (!!onHMirror && (!meta || !!meta[placedButtons.flipW])) {
        buttons.push(<TopButton
            onClick={(e) => {
                e.stopPropagation();
                onVMirror(placedItem);
            }}
            style={{
                position: 'absolute',
                top: 'calc(50% + 3px)',
                [!meta ? 'left' : meta[placedButtons.flipW]]: -60,
                width: 40,
                height: 40,
            }}>
            <VMirror/>
        </TopButton>)
    }


    cachedControls[placedItem.uuid] = <>
        {buttons}
    </>;
    return cachedControls[placedItem.uuid];
}

export default PlacedItemView;
